import { useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useActiveSection } from '../../../../Hooks/Hooks/useActiveSection';

const BLOCK = 'LStudio__app_header';

/*
    ⚠️ EL SCROLLSPY TIENE QUE DESCONTAR EL HEADER FIJO

       Sin esto una sección se marca activa cuando su borde superior todavía está
       TAPADO por el header: la marca salta antes de que llegues a ver la sección.

       El alto sale de tu propia variable, así que si cambiás el header en
       VariablesLight/Dark esto acompaña solo.
*/
const onHeaderOffset = () => {

    if (typeof window === 'undefined') return 0;

    const RAW = getComputedStyle(document.documentElement).getPropertyValue('--LS__app_header_public_height');

    return parseInt(RAW, 10) || 0;
};

/*
    🔥 SUBLINK QUE HACE SCROLL A UNA SECCIÓN

    ⚠️ Los hijos con hash son anclas dentro de la MISMA página (los
       scrollToItems que declara cada módulo). react-router no scrollea solo al
       hash, así que hay que hacerlo a mano.

    ⚠️ Si ya estás en la página destino, NO se navega: sólo se scrollea. Navegar
       dispararía el ScrollToTop del routing y te mandaría arriba de todo, que
       es justo lo contrario de lo que hace falta.

    ⚠️ Si venís de otra página, primero se navega y recién cuando la sección
       existe en el DOM se scrollea. Por eso el reintento: la vista tarda unos
       frames en montar.
*/
const onScrollToSection = (hash, attempt = 0) => {

    const EL = document.getElementById(hash);

    if (EL) {
        EL.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
    }

    if (attempt < 20) window.requestAnimationFrame(() => onScrollToSection(hash, attempt + 1));
};

const NavSubLink = ({ child, onNavigate, isInline = false, isActive = false }) => {

    const navigate = useNavigate();
    const { pathname } = useLocation();

    const onClick = (event) => {

        if (!child.hash) return onNavigate?.();

        event.preventDefault();

        if (pathname !== child.parentPath) navigate(child.parentPath);

        onScrollToSection(child.hash);
        onNavigate?.();
    };

    return (
        <Link to={child.path} onClick={onClick}
            className={
                isInline
                    ? `${BLOCK}__link ${BLOCK}__link--section ${isActive ? `${BLOCK}__link--active` : ''}`
                    : `${BLOCK}__sublink`
            }>
            {child.label}
        </Link>
    );
};

/*
    🔥 LA MISMA LISTA EN LOS CUATRO MODOS

    inline   -> caret DENTRO del link, submenú flotante al pasar el mouse
                (idéntico al comportamiento original)
    vertical -> caret como botón aparte, submenú acordeón al tocar
                (necesario en panel/offcanvas: en touch no hay hover)
*/

// ---------------------------- 🔥 ITEM EN LÍNEA
const NavItemInline = ({ item, isActive }) => {

    const [useIsOpen, setIsOpen] = useState(false);

    const HAS_CHILDREN = (item.children ?? []).length > 0;

    if (!HAS_CHILDREN) {
        return (
            <Link to={item.path} className={`${BLOCK}__link ${isActive ? `${BLOCK}__link--active` : ''}`}>
                {item.label}
            </Link>
        );
    }

    return (
        <div className={`${BLOCK}__group`}
            onMouseEnter={() => setIsOpen(true)} onMouseLeave={() => setIsOpen(false)} >

            <Link to={item.path} className={`${BLOCK}__link ${isActive ? `${BLOCK}__link--active` : ''}`}>
                {item.label}
                <FaChevronDown className={`${BLOCK}__caret`} aria-hidden="true" />
            </Link>

            <div className={`${BLOCK}__submenu`} data-open={useIsOpen}>
                {
                    item.children.map((child) => (
                        <NavSubLink key={child.path} child={child} />
                    ))
                }
            </div>

        </div>
    );
};

// ---------------------------- 🔥 ITEM VERTICAL
const NavItemVertical = ({ item, isActive, onNavigate }) => {

    const [useIsOpen, setIsOpen] = useState(false);

    const HAS_CHILDREN = (item.children ?? []).length > 0;

    if (!HAS_CHILDREN) {
        return (
            <Link to={item.path} onClick={onNavigate}
                className={`${BLOCK}__link ${isActive ? `${BLOCK}__link--active` : ''}`} >
                {item.label}
            </Link>
        );
    }

    return (
        <div className={`${BLOCK}__group`}>

            <div className={`${BLOCK}__group_head`}>

                <Link to={item.path} onClick={onNavigate}
                    className={`${BLOCK}__link ${isActive ? `${BLOCK}__link--active` : ''}`} >
                    {item.label}
                </Link>

                <button type="button" className={`${BLOCK}__caret_button`}
                    aria-label={`Desplegar ${item.label}`} aria-expanded={useIsOpen}
                    onClick={() => setIsOpen(!useIsOpen)} >
                    <FaChevronDown className={`${BLOCK}__caret`} data-open={useIsOpen} aria-hidden="true" />
                </button>

            </div>

            <div className={`${BLOCK}__submenu`} data-open={useIsOpen}>
                {
                    item.children.map((child) => (
                        <NavSubLink key={child.path} child={child} onNavigate={onNavigate} />
                    ))
                }
            </div>

        </div>
    );
};

// ---------------------------- 🔥 LISTA
export const NavList = ({ items = [], isVertical = false, onNavigate = null }) => {

    const { pathname } = useLocation();

    const onIsActive = (path) => (path === '/' ? pathname === '/' : pathname.startsWith(path));

    /*
        🔥 LOS ANCLAS DE SECCIÓN VAN AL LADO, NO EN UN DESPLEGABLE

        ⚠️ Los hijos con hash (los scrollToItems del módulo) NO son un submenú:
           son atajos a secciones de la MISMA página. Van sueltos en la barra,
           a la derecha del ítem que los trae.

        ⚠️ SÓLO SE MUESTRAN CUANDO ESTÁS EN ESA PÁGINA. Apuntan a secciones que
           en otra ruta no existen: mostrarlos ahí sería ofrecer enlaces muertos.
           Por eso se cuelan únicamente si el ítem está activo.

        ⚠️ Un ítem con hijos SIN hash sigue siendo un submenú normal, con su
           caret y su desplegable. No se toca ese caso.
    */
    const onIsScrollGroup = (item) => (item.children ?? []).some((child) => !!child.hash);

    /*
        🔥 SCROLLSPY EN LA BARRA

        ⚠️ Los ids salen de los propios hijos con hash, así que el header no
           necesita saber nada de la vista: si un módulo suma una sección, el
           atajo y su marca de activo aparecen solos.

        ⚠️ Se observan sólo los del ítem activo. Los de otras rutas no existen
           en el DOM y el observer los ignoraría igual, pero así tampoco se
           enganchan de más.
    */
    const SECTION_IDS = items
        .filter((item) => onIsScrollGroup(item) && onIsActive(item.path))
        .flatMap((item) => item.children.map((child) => child.hash));

    const ACTIVE_SECTION = useActiveSection(SECTION_IDS, { offsetTop: onHeaderOffset() });

    const ITEMS = items.flatMap((item) => {

        if (!onIsScrollGroup(item)) return [{ type: 'item', node: item }];

        const PADRE = [{ type: 'item', node: { ...item, children: [] } }];

        if (!onIsActive(item.path)) return PADRE;

        return PADRE.concat(item.children.map((child) => ({ type: 'section', node: child })));
    });

    return (
        <div className={`${BLOCK}__list`} data-vertical={isVertical}>
            {
                ITEMS.map(({ type, node }) => (
                    type === 'section'
                        ? <NavSubLink key={node.path} child={node} onNavigate={onNavigate}
                            isInline isActive={ACTIVE_SECTION === node.hash} />
                        : isVertical
                            ? <NavItemVertical key={node.path} item={node}
                                isActive={onIsActive(node.path)} onNavigate={onNavigate} />
                            : <NavItemInline key={node.path} item={node}
                                isActive={onIsActive(node.path)} />
                ))
            }
        </div>
    );
};
